import { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Check, Plus, Trash, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function ExamWeightConfig() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('examweightconfig');
  const isRTL = i18n.language === 'ar';
  
  const [academicYears, setAcademicYears] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [configName, setConfigName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedExams, setSelectedExams] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Filter states
  const [availableTerms, setAvailableTerms] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);

  // Fetch academic years with terms
  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/v1/academic-years/list-with-terms');
        setAcademicYears(response.data || []);
        
        // Set default to current year if available
        const currentYear = response.data?.find(year => year.is_current_year);
        if (currentYear) {
          setSelectedYear(currentYear.academic_year_id.toString());
          setAvailableTerms(currentYear.terms || []);
          if (currentYear.terms?.length > 0) {
            setSelectedTerm(currentYear.terms[0].term_id.toString());
          }
        }
      } catch (err) {
        setError(t('errors.failedLoadAcademicYears'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAcademicYears();
  }, [api, t]);

  // Fetch all exams
  useEffect(() => {
    const fetchExams = async () => {
      if (!user?.school_id) return;
      
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/exams/school/${user.school_id}/all-exams`);
        setExams(response.data || []);
      } catch (err) {
        setError(t('errors.failedLoadExams'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, [api, user, t]);

  // Update available terms when year changes
  useEffect(() => {
    if (selectedYear) {
      const year = academicYears.find(y => y.academic_year_id.toString() === selectedYear);
      setAvailableTerms(year?.terms || []);
      setSelectedTerm(year?.terms?.[0]?.term_id.toString() || '');
    } else {
      setAvailableTerms([]);
      setSelectedTerm('');
    }
  }, [selectedYear, academicYears]);

  // Filter exams based on selected academic year and term
  useEffect(() => {
    if (selectedYear && selectedTerm) {
      const filtered = exams.filter(
        exam => 
          exam.academic_year_id.toString() === selectedYear &&
          exam.term_id.toString() === selectedTerm
      );
      setFilteredExams(filtered);
    } else {
      setFilteredExams([]);
    }
  }, [selectedYear, selectedTerm, exams]);

  // Handler for adding exam with weight
  const handleAddExam = (exam) => {
    // Check if exam is already added
    if (selectedExams.some(e => e.exam_id === exam.id)) {
      return;
    }
    
    setSelectedExams([
      ...selectedExams,
      {
        exam_id: exam.id,
        exam_name: exam.exam_name,
        weight: 0
      }
    ]);
  };

  // Handler for removing exam
  const handleRemoveExam = (examId) => {
    setSelectedExams(selectedExams.filter(exam => exam.exam_id !== examId));
  };

  // Handler for updating exam weight
  const handleWeightChange = (examId, value) => {
    // Convert to float with two decimal places
    let weight = parseFloat(value) || 0;
    
    // Ensure weight is between 0 and 1
    weight = Math.max(0, Math.min(1, weight));
    
    setSelectedExams(
      selectedExams.map(exam => 
        exam.exam_id === examId ? { ...exam, weight } : exam
      )
    );
  };

  // Calculate total weight
  const totalWeight = selectedExams.reduce((sum, exam) => sum + (exam.weight || 0), 0);
  // Format total weight to display up to 2 decimal places
  const formattedTotalWeight = totalWeight.toFixed(2);

  // Handler for form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedExams.length === 0) {
      setSubmitError({
        detail: [{ msg: t('errors.selectAtLeastOneExam') }]
      });
      return;
    }

    // Check if total weight is 1.0 (with a small margin for floating point errors)
    if (Math.abs(totalWeight - 1) > 0.001) {
      setSubmitError({
        detail: [{ msg: t('errors.totalWeightMustEqual') }]
      });
      return;
    }

    if (!configName.trim()) {
      setSubmitError({
        detail: [{ msg: t('errors.provideConfigName') }]
      });
      return;
    }

    try {
      setSubmitLoading(true);
      setSubmitError(null);
      
      const payload = {
        config_name: configName,
        description: description,
        weights: selectedExams.map(exam => ({
          exam_id: exam.exam_id,
          weight: exam.weight
        }))
      };

      await api.post('/api/v1/exam-analysis/weight-configs', payload);
      
      // Reset form on success
      setConfigName('');
      setDescription('');
      setSelectedExams([]);
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setSubmitError(err.response?.data || { 
        detail: [{ msg: t('errors.savingError') }] 
      });
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading && !academicYears.length && !exams.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-pulse text-teal-800">{t('loading.examData')}</div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('title')}</h2>
      
      {/* Error display */}
      {(error || submitError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <div className="flex items-center mb-1">
            <X size={18} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            <span className="font-medium">{t('error.title')}</span>
          </div>
          <ul className={`list-disc ${isRTL ? 'list-inside mr-2' : 'list-inside ml-2'} text-sm`}>
            {error && <li>{error}</li>}
            {submitError?.detail?.map((err, idx) => (
              <li key={idx}>{err.msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <Check size={18} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
          <span>{t('success.configSaved')}</span>
        </div>
      )}

      <div onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Left column - Selection area */}
          <div>
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">{t('steps.selectExams')}</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Academic Year dropdown */}
                <div>
                  <label htmlFor="academicYear" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.academicYear')}
                  </label>
                  <select 
                    id="academicYear"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  >
                    <option value="">{t('placeholders.selectAcademicYear')}</option>
                    {academicYears.map((year) => (
                      <option key={year.academic_year_id} value={year.academic_year_id}>
                        {year.year_name} {year.is_current_year ? t('labels.current') : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Term dropdown */}
                <div>
                  <label htmlFor="term" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('fields.term')}
                  </label>
                  <select 
                    id="term"
                    value={selectedTerm}
                    onChange={(e) => setSelectedTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    disabled={!selectedYear || availableTerms.length === 0}
                  >
                    <option value="">{t('placeholders.selectTerm')}</option>
                    {availableTerms.map((term) => (
                      <option key={term.term_id} value={term.term_id}>
                        {term.term_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Available Exams Section */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700">{t('sections.availableExams')}</h4>
                  <span className="text-xs text-gray-500">
                    {t('labels.examsFound', { count: filteredExams.length })}
                  </span>
                </div>
                
                {filteredExams.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    {selectedYear && selectedTerm ? t('messages.noExamsFound') : t('messages.selectYearAndTerm')}
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className={`px-3 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                            {t('table.examName')}
                          </th>
                          <th className={`px-3 py-2 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider w-24`}>
                            {t('table.action')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredExams.map((exam) => {
                          const isSelected = selectedExams.some(e => e.exam_id === exam.id);
                          
                          return (
                            <tr key={exam.id} className={isSelected ? 'bg-teal-50' : ''}>
                              <td className={`px-3 py-2 text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {exam.exam_name}
                              </td>
                              <td className={`px-3 py-2 ${isRTL ? 'text-left' : 'text-right'}`}>
                                <button
                                  type="button"
                                  onClick={() => handleAddExam(exam)}
                                  disabled={isSelected}
                                  className={`inline-flex items-center p-1 rounded-md ${
                                    isSelected 
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                      : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
                                  }`}
                                >
                                  <Plus size={16} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column - Weight Configuration */}
          <div>
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">{t('steps.configureWeights')}</h3>
              
              <div className="mb-4">
                <label htmlFor="configName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.configurationName')} *
                </label>
                <input
                  type="text"
                  id="configName"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder={t('placeholders.configName')}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.description')}
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 h-20 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder={t('placeholders.description')}
                />
              </div>

              {/* Selected Exams with Weight Input */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700">{t('sections.selectedExams')}</h4>
                  <div className="flex items-center">
                    <span className={`text-xs font-medium ${Math.abs(totalWeight - 1) <= 0.001 ? 'text-green-600' : 'text-amber-600'}`}>
                      {t('labels.total')}: {formattedTotalWeight}
                    </span>
                    <div className={`${isRTL ? 'mr-2' : 'ml-2'} w-2 h-2 rounded-full bg-gray-300`}>
                      <div className={`h-full rounded-full ${Math.abs(totalWeight - 1) <= 0.001 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(totalWeight * 100, 100)}%` }}></div>
                    </div>
                  </div>
                </div>
                
                {selectedExams.length === 0 ? (
                  <div className="text-center py-6 text-sm text-gray-500">
                    {t('messages.noExamsSelected')}
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className={`px-3 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                            {t('table.examName')}
                          </th>
                          <th className={`px-3 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider w-24`}>
                            {t('table.weight')}
                          </th>
                          <th className={`px-3 py-2 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider w-16`}>
                            {t('table.action')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedExams.map((exam) => (
                          <tr key={exam.exam_id}>
                            <td className={`px-3 py-2 text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {exam.exam_name}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.01"
                                value={exam.weight}
                                onChange={(e) => handleWeightChange(exam.exam_id, e.target.value)}
                                className="w-full border border-gray-300 rounded p-1 text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                              />
                            </td>
                            <td className={`px-3 py-2 ${isRTL ? 'text-left' : 'text-right'}`}>
                              <button
                                type="button"
                                onClick={() => handleRemoveExam(exam.exam_id)}
                                className="inline-flex items-center p-1 rounded-md bg-red-50 text-red-600 hover:bg-red-100"
                              >
                                <Trash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {selectedExams.length > 0 && Math.abs(totalWeight - 1) > 0.001 && (
                  <div className={`mt-3 text-xs text-amber-600 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <svg className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('warnings.totalWeightMustEqual')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className={`border-t border-gray-200 pt-4 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
          <button
            type="submit"
            disabled={submitLoading}
            className={`flex items-center px-4 py-2 rounded-md bg-[#0E5252] text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 ${
              submitLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {submitLoading ? (
              <>
                <svg className={`animate-spin ${isRTL ? '-mr-1 ml-2' : '-ml-1 mr-2'} h-4 w-4 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('buttons.saving')}
              </>
            ) : (
              <>
                {t('buttons.saveConfiguration')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExamWeightConfig;